import { useEffect, useRef, useState } from "react";
import Styles from "./PhoneInput.module.css";

const formatUS = (digits) => {
	if (digits.length <= 3) return digits;
	if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
	return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const formatIntl = (digits) => digits.replace(/(\d{3})(?=\d)/g, "$1 ");

/**
 * Phone input with editable country code + auto-formatting.
 * Renders a hidden input with E.164 value (e.g. "+11234567890") under `name`
 * for form submission. Defaults to US (+1) with XXX-XXX-XXXX formatting.
 */
const PhoneInput = ({ id, name, label, required }) => {
	const [dialCode, setDialCode] = useState("1");
	const [digits, setDigits] = useState("");
	const phoneRef = useRef(null);

	const isUS = dialCode === "1";
	const maxDigits = isUS ? 10 : 14;
	const minDigits = isUS ? 10 : 7;
	const displayValue = isUS ? formatUS(digits) : formatIntl(digits);
	const hiddenValue = digits ? `+${dialCode}${digits}` : "";

	const handleDialChange = (e) => {
		const next = e.target.value.replace(/\D/g, "").slice(0, 4);
		setDialCode(next);
	};

	const handleDialBlur = () => {
		if (!dialCode) setDialCode("1");
	};

	const handlePhoneChange = (e) => {
		const next = e.target.value.replace(/\D/g, "").slice(0, maxDigits);
		setDigits(next);
	};

	useEffect(() => {
		if (!phoneRef.current) return;
		if (required && !digits) {
			phoneRef.current.setCustomValidity("");
		} else if (digits && digits.length < minDigits) {
			phoneRef.current.setCustomValidity(
				`Please enter at least ${minDigits} digits.`,
			);
		} else {
			phoneRef.current.setCustomValidity("");
		}
	}, [digits, minDigits, required]);

	return (
		<div className={Styles.wrapper}>
			{label && <label htmlFor={id}>{label}</label>}
			<div className={Styles.inputRow}>
				<div className={Styles.dialGroup}>
					<span className={Styles.plus}>+</span>
					<input
						type="text"
						inputMode="numeric"
						aria-label="Country code"
						className={Styles.dialInput}
						value={dialCode}
						onChange={handleDialChange}
						onBlur={handleDialBlur}
						maxLength={4}
					/>
				</div>
				<input
					ref={phoneRef}
					id={id}
					type="tel"
					inputMode="numeric"
					autoComplete="tel-national"
					className={Styles.phoneInput}
					placeholder={isUS ? "123-456-7890" : "Phone number"}
					value={displayValue}
					onChange={handlePhoneChange}
					required={required}
				/>
				<input type="hidden" name={name} value={hiddenValue} />
			</div>
		</div>
	);
};

export default PhoneInput;
